import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import DashboardLayout from "../components/DashboardLayout";
import { api } from "../lib/apiClient";
import { AGENTS } from "../data/agents";
import AgentIcon from "../components/AgentIcon";
import { CheckCircle2, AlertTriangle, HelpCircle, Sparkles, FileText } from "lucide-react";
import { toast } from "sonner";
import { useT } from "../lib/i18n";

const conf = (n) => {
  if (n >= 80) return "text-[#10B981]";
  if (n >= 60) return "text-[#0EA5E9]";
  if (n >= 40) return "text-[#F59E0B]";
  return "text-[#EF4444]";
};

export default function SessionWorkspace() {
  const { t } = useT();
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [running, setRunning] = useState(false);
  const [activeRound, setActiveRound] = useState(1);
  const startedRef = useRef(false);
  const pollTimer = useRef(null);

  const fetchSession = useCallback(async () => {
    const r = await api.get(`/sessions/${id}`);
    setSession(r.data);
    if (r.data.round_outputs?.length) setActiveRound(r.data.round_outputs[r.data.round_outputs.length - 1].round);
    return r.data;
  }, [id]);

  const stopPolling = useCallback(() => {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  const startPolling = useCallback(() => {
    if (pollTimer.current) return;
    setRunning(true);
    pollTimer.current = setInterval(async () => {
      try {
        const r = await api.get(`/sessions/${id}`);
        setSession(r.data);
        if (r.data.round_outputs?.length) setActiveRound(r.data.round_outputs[r.data.round_outputs.length - 1].round);
        if (r.data.status === "completed") {
          stopPolling();
          setRunning(false);
          toast.success("Session completed");
        } else if (r.data.status === "failed") {
          stopPolling();
          setRunning(false);
          toast.error(r.data.error || "Run failed. Credits have been refunded.");
        }
      } catch (e) { /* transient poll error — keep polling */ }
    }, 3000);
  }, [id, stopPolling]);

  const run = useCallback(async () => {
    if (running) return;
    setRunning(true);
    toast.info(t("Running Konzenzo rounds — progress is saved, you can safely leave this page."));
    try {
      const r = await api.post(`/sessions/${id}/run`);
      setSession(r.data);
      if (r.data.status === "completed") {
        setActiveRound(r.data.round_outputs?.[r.data.round_outputs.length - 1]?.round || 1);
        setRunning(false);
        toast.success(t("Session completed"));
      } else {
        startPolling();
      }
    } catch (e) {
      setRunning(false);
      toast.error(e?.response?.data?.detail || t("Run failed"));
    }
  }, [id, running, startPolling, t]);

  useEffect(() => {
    (async () => {
      const s = await fetchSession();
      if (s.status === "running") {
        startPolling();
      } else if (params.get("run") === "1" && s.status !== "completed" && !startedRef.current) {
        startedRef.current = true;
        run();
      }
    })();
    return stopPolling;
  }, [fetchSession, params, run, startPolling, stopPolling]);

  if (!session) return <DashboardLayout title={t("Loading…")}><div className="text-[#94A3B8]">{t("Loading…")}</div></DashboardLayout>;

  const rounds = session.round_outputs || [];
  const current = rounds.find((r) => r.round === activeRound);

  return (
    <DashboardLayout
      title={session.title}
      action={
        <div className="flex items-center gap-3">
          {session.status !== "completed" && (
            <button data-testid="run-session-btn" onClick={run} disabled={running} className="bg-[#10B981] disabled:opacity-50 text-white hover:bg-[#059669] px-5 py-2.5 rounded-lg font-bold text-sm font-display inline-flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> {running ? t("Running…") : t("Run Konzenzo")}
            </button>
          )}
          {session.status === "completed" && (
            <button data-testid="view-report-btn" onClick={() => navigate(`/sessions/${id}/report`)} className="bg-[#0B1B3D] text-white hover:bg-[#1E3A8A] px-5 py-2.5 rounded-lg font-bold text-sm font-display inline-flex items-center gap-2">
              <FileText className="w-4 h-4" /> {t("View report")}
            </button>
          )}
        </div>
      }
    >
      <div className="grid lg:grid-cols-12 gap-6">
        {session.status === "failed" && !running && (
          <div data-testid="run-failed-banner" className="lg:col-span-12 bg-[#EF4444]/10 border border-[#EF4444]/30 rounded-xl p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-[#EF4444] shrink-0" />
            <p className="text-sm text-[#0B1B3D] font-medium">{session.error || t("The last run failed. Credits were refunded — you can run the session again.")}</p>
          </div>
        )}
        <aside className="lg:col-span-3 space-y-4">
          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <p className="font-mono-kz text-xs text-[#0EA5E9] tracking-widest font-bold">{t("KONZENZO ROUNDS")}</p>
            <div className="mt-4 flex flex-col gap-2">
              {[1, 2, 3].slice(0, session.rounds).map((r) => {
                const has = rounds.some((x) => x.round === r);
                const labels = { 1: "Independent views", 2: "Refinement", 3: "Final consensus" };
                let cls = "border-dashed border-slate-200 opacity-50";
                if (activeRound === r) cls = "border-[#0B1B3D] bg-[#0B1B3D] text-white";
                else if (has) cls = "border-slate-200 hover:border-[#0EA5E9]";
                return (
                  <button key={r} data-testid={`round-tab-${r}`} onClick={() => has && setActiveRound(r)} disabled={!has} className={`text-left p-3 rounded-lg border transition-all ${cls}`}>
                    <p className="font-mono-kz text-xs tracking-widest">{t("ROUND")} {r}</p>
                    <p className="font-display font-bold mt-1">{t(labels[r])}</p>
                    {!has && <p className="text-xs mt-1 opacity-70">{running ? t("running…") : t("pending")}</p>}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-5">
            <p className="font-mono-kz text-xs text-[#0EA5E9] tracking-widest font-bold">PANEL</p>
            <div className="mt-3 space-y-2">
              {session.agent_ids.map((aid) => {
                const a = AGENTS.find((x) => x.id === aid);
                if (!a) return null;
                return (
                  <div key={aid} className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-md bg-[#0B1B3D] grid place-items-center text-white"><AgentIcon name={a.icon} className="w-3.5 h-3.5" /></div>
                    <p className="text-sm font-bold text-[#0B1B3D]">{a.name}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {session.documents?.length > 0 && (
            <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="session-documents-card">
              <p className="font-mono-kz text-xs text-[#0EA5E9] tracking-widest font-bold">{t("DOCUMENTS")}</p>
              <div className="mt-3 space-y-2">
                {session.documents.map((d, i) => (
                  <div key={`${d.filename}-${i}`} className="flex items-center gap-2.5">
                    <FileText className="w-4 h-4 text-[#94A3B8] shrink-0" />
                    <p className="text-sm font-bold text-[#0B1B3D] truncate" title={d.filename}>{d.filename}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </aside>

        <section className="lg:col-span-9">
          {!current ? (
            <div className="bg-white border border-dashed border-slate-200 rounded-xl p-12 text-center">
              <Sparkles className="w-8 h-8 mx-auto text-[#0EA5E9]" />
              <p className="mt-3 font-display font-bold text-[#0B1B3D]">{running ? t("Agents are deliberating…") : t("Run the Konzenzo session to see results")}</p>
              <p className="text-sm text-[#475569] mt-1">{t("Round outputs appear here as soon as agents complete each phase.")}</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid={`round-content-${current.round}`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-mono-kz text-xs text-[#0EA5E9] tracking-widest font-bold">{t("ROUND")} {current.round}</p>
                  <h2 className="font-display text-2xl font-extrabold text-[#0B1B3D] mt-1">{current.label}</h2>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {current.agents.map((row) => {
                  const a = AGENTS.find((x) => x.id === row.agent_id);
                  return (
                    <div key={row.agent_id} data-testid={`agent-output-${row.agent_id}`} className="bg-white border border-slate-200 rounded-xl p-5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-md bg-[#0B1B3D] grid place-items-center text-white">
                            <AgentIcon name={a?.icon || "Sparkles"} className="w-4 h-4" />
                          </div>
                          <div>
                            <p className="font-display font-extrabold text-[#0B1B3D] leading-tight">{row.agent_name}</p>
                            <p className="text-xs text-[#94A3B8] font-mono-kz tracking-widest">{a?.specialty}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-mono-kz text-xs text-[#94A3B8]">CONFIDENCE</p>
                          <p className={`font-display font-black text-xl ${conf(row.confidence || 0)}`}>{Math.round(row.confidence || 0)}%</p>
                        </div>
                      </div>
                      <p className="mt-4 text-sm text-[#475569] leading-relaxed">{row.opinion}</p>
                      {row.recommendation && (
                        <div className="mt-3 rounded-md bg-[#10B981]/10 border border-[#10B981]/30 p-3">
                          <p className="text-xs font-mono-kz tracking-widest text-[#10B981] font-bold">RECOMMENDATION</p>
                          <p className="text-sm text-[#0B1B3D] mt-1 font-medium">{row.recommendation}</p>
                        </div>
                      )}
                      {!!(row.risks && row.risks.length) && (
                        <div className="mt-3">
                          <p className="text-xs font-mono-kz tracking-widest text-[#EF4444] font-bold flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> RISKS</p>
                          <ul className="mt-1 text-sm text-[#475569] list-disc list-inside space-y-1">{row.risks.map((rk) => <li key={rk}>{rk}</li>)}</ul>
                        </div>
                      )}
                      {!!(row.key_assumptions && row.key_assumptions.length) && (
                        <div className="mt-3">
                          <p className="text-xs font-mono-kz tracking-widest text-[#0EA5E9] font-bold flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> ASSUMPTIONS</p>
                          <ul className="mt-1 text-sm text-[#475569] list-disc list-inside space-y-1">{row.key_assumptions.map((ka) => <li key={ka}>{ka}</li>)}</ul>
                        </div>
                      )}
                      {!!(row.questions && row.questions.length) && (
                        <div className="mt-3">
                          <p className="text-xs font-mono-kz tracking-widest text-[#F59E0B] font-bold flex items-center gap-1"><HelpCircle className="w-3 h-3" /> OPEN QUESTIONS</p>
                          <ul className="mt-1 text-sm text-[#475569] list-disc list-inside space-y-1">{row.questions.map((q) => <li key={q}>{q}</li>)}</ul>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      </div>
    </DashboardLayout>
  );
}

